import { useManager } from 'dreamstate';
import { useMemo } from 'react';
import { useState, useCallback, useEffect } from 'react';

import { Optional } from '@vimeo/core/types/generic';

import { EQuestionState, IQnAQuestion, TQnAQuestions } from '../../../../types/interaction_tools';
import { scrollElementIntoView } from '../../../../utils/dom/scroll';
import { QnAManager } from './QnAManager';

export function useQnaQuestions(
  activeSessionPendingQuestions: TQnAQuestions,
  activeSessionQuestions: TQnAQuestions,
) {
  const activeQuestions: Array<IQnAQuestion> = useMemo(
    () =>
      Object.values(activeSessionQuestions).filter(
        (question) =>
          question.state === EQuestionState.ASKED || question.state === EQuestionState.ANSWERING,
      ),
    [activeSessionQuestions],
  );

  const pendingQuestions: Array<IQnAQuestion> = useMemo(
    () =>
      Object.values(activeSessionPendingQuestions).filter(
        (question: IQnAQuestion) => question.state === EQuestionState.PENDING,
      ),
    [activeSessionPendingQuestions],
  );

  const archivedQuestions: Array<IQnAQuestion> = useMemo(() => {
    const allQuestions: TQnAQuestions = {
      ...activeSessionPendingQuestions,
      ...activeSessionQuestions,
    };
    return Object.values(allQuestions).filter(
      (questions: IQnAQuestion) => questions.state === EQuestionState.ARCHIVED,
    );
  }, [activeSessionPendingQuestions, activeSessionQuestions]);

  return {
    activeQuestions,
    pendingQuestions,
    archivedQuestions,
    count: activeQuestions.length + pendingQuestions.length + archivedQuestions.length,
  };
}

/**
 * Handle selection / deselection questions in managed list.
 */
export function useQnaResponding() {
  const { activeSessionQuestions, activeSessionPendingQuestions } = useManager(QnAManager);

  const [selectedQuestionState, setSelectedQuestionState] =
    useState<Optional<EQuestionState>>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<Optional<string>>(null);
  const [selectedQuestionElement, setSelectedQuestionElement] =
    useState<Optional<HTMLDivElement>>(null);

  const onSelectedQuestion = useCallback((question: IQnAQuestion, element: HTMLDivElement) => {
    setSelectedQuestionId(question.id);
    setSelectedQuestionState(question.state);
    setSelectedQuestionElement(element);

    const itemElement: HTMLElement = element.parentElement as HTMLElement;
    const listScroll: HTMLElement = itemElement?.parentElement?.parentElement as HTMLElement;

    const isElementScrolledUp: boolean = listScroll.scrollTop > itemElement.offsetTop;
    const isElementScrolledDown: boolean =
      itemElement.offsetTop + itemElement.clientHeight >
      listScroll.scrollTop + listScroll.clientHeight;

    if (isElementScrolledUp || isElementScrolledDown) {
      setTimeout(() =>
        scrollElementIntoView(element, {
          behavior: 'smooth',
          block: isElementScrolledUp ? 'start' : isElementScrolledDown ? 'end' : 'center',
        }),
      );
    }
  }, []);

  const onDeselectQuestion = useCallback(() => {
    setSelectedQuestionId(null);
    setSelectedQuestionState(null);
    setSelectedQuestionElement(null);
  }, []);

  useEffect(() => {
    const questions = { ...activeSessionQuestions, ...activeSessionPendingQuestions };
    const selectedQuestion: IQnAQuestion = questions[selectedQuestionId as string];

    if (!selectedQuestion || selectedQuestionState !== selectedQuestion.state) {
      onDeselectQuestion();
    }
  }, [
    onDeselectQuestion,
    activeSessionQuestions,
    activeSessionPendingQuestions,
    selectedQuestionId,
    selectedQuestionState,
  ]);

  return {
    selectedQuestionId,
    selectedQuestionState,
    selectedQuestionElement,
    onSelectedQuestion,
    onDeselectQuestion,
  };
}
